import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { QRCodeDisplay } from "@/components/QRCodeDisplay";
import { MechanicalChecklistDisplay, calculateAverageScore, getOverallCondition } from "@/components/MechanicalChecklist";
import { PrintScooterSheet } from "@/components/PrintScooterSheet";
import { PublishingCenter } from "@/components/PublishingCenter";
import { OilChangeHistory } from "@/components/OilChangeHistory";
import { ServiceAlerts } from "@/components/ServiceAlerts";
import { SaleRecord } from "@/components/SaleRecord";
import { SalesCenter } from "@/components/SalesCenter";
import { SortablePhotoGallery } from "@/components/SortablePhotoGallery";
import { RepairTasksManager } from "@/components/RepairTasksManager";
import { AIDescriptionButton, AISocialPostButton } from "@/components/AITools";
import { AIBackgroundButton, AISocialBannerButton, AIPromoVisualButton } from "@/components/AIImageTools";
import { scooterApi, formatPrice, getFileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { 
  ArrowLeft,
  Edit,
  Trash2,
  Camera,
  FileText,
  Settings,
  Wrench,
  History,
  Upload,
  X,
  QrCode,
  Gauge,
  Calendar,
  Fuel,
  Palette,
  Hash,
  Download,
  Printer,
  Share2,
  Droplet,
  Bell,
  DollarSign,
  Lock,
  ShoppingCart,
  PackageOpen,
  CheckCircle
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function ScooterDetailPage() {
  const { scooterId } = useParams();
  const navigate = useNavigate();
  const { isAdmin, canPublish } = useAuth();
  const [scooter, setScooter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const photoInputRef = useRef(null);
  const docInputRef = useRef(null);

  useEffect(() => {
    fetchScooter();
  }, [scooterId]);

  const fetchScooter = async () => {
    try {
      const data = await scooterApi.getById(scooterId);
      setScooter(data);
    } catch (error) {
      toast.error("Scooter not found");
      navigate("/stock");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await scooterApi.delete(scooterId);
      toast.success("Scooter deleted");
      navigate("/stock");
    } catch (error) {
      toast.error("Failed to delete scooter");
    }
  };

  // Compress image before upload
  const compressImage = (file, maxWidth = 1600, quality = 0.75) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => {
              if (blob) {
                // Create a new file from the blob
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                console.log(`Compressed: ${file.name} - ${(file.size/1024).toFixed(0)}KB → ${(blob.size/1024).toFixed(0)}KB`);
                resolve(compressedFile);
              } else {
                reject(new Error('Compression failed'));
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const totalFiles = files.length;
    let uploadedCount = 0;
    let failedCount = 0;
    
    setUploadProgress({ current: 0, total: totalFiles });
    
    // Process and upload all files
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ current: i + 1, total: totalFiles });
      
      try {
        // Reasonable compression for good quality photos
        let fileToUpload = file;
        if (file.type.startsWith('image/')) {
          toast.info(`Compression ${i + 1}/${totalFiles}: ${file.name}...`);
          // Good quality: max 1600px, quality 0.75
          fileToUpload = await compressImage(file, 1600, 0.75);
        }
        
        console.log(`Uploading ${i + 1}/${totalFiles}: ${file.name} (${(fileToUpload.size/1024).toFixed(0)}KB)`);
        
        const result = await scooterApi.uploadPhoto(scooterId, fileToUpload);
        console.log('Upload result:', result);
        uploadedCount++;
      } catch (error) {
        console.error('Upload error for', file.name, ':', error);
        failedCount++;
      }
    }
    
    // Show result message
    if (uploadedCount > 0 && failedCount === 0) {
      toast.success(`✅ ${uploadedCount} photo${uploadedCount > 1 ? 's' : ''} téléchargée${uploadedCount > 1 ? 's' : ''} avec succès!`);
    } else if (uploadedCount > 0 && failedCount > 0) {
      toast.warning(`${uploadedCount} photo${uploadedCount > 1 ? 's' : ''} OK, ${failedCount} échec${failedCount > 1 ? 's' : ''}`);
    } else {
      toast.error("Échec du téléchargement des photos");
    }
    
    setUploadProgress({ current: 0, total: 0 });
    fetchScooter();
    setUploading(false);
    if (photoInputRef.current) photoInputRef.current.value = "";
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      await scooterApi.uploadDocument(scooterId, file);
      toast.success("Document uploaded");
      fetchScooter();
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
      if (docInputRef.current) docInputRef.current.value = "";
    }
  };

  const handleDeletePhoto = async (photoUrl) => {
    const filename = photoUrl.split("/").pop();
    try {
      await scooterApi.deletePhoto(scooterId, filename);
      toast.success("Photo deleted");
      fetchScooter();
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  const handleDeleteDoc = async (docUrl) => {
    const filename = docUrl.split("/").pop();
    try {
      await scooterApi.deleteDocument(scooterId, filename);
      toast.success("Document deleted");
      fetchScooter();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-pulse text-primary font-mono">Loading...</div>
        </div>
      </Layout>
    );
  }

  if (!scooter) return null;

  const defaultImage = "https://images.unsplash.com/photo-1608551883326-2c9bd5d52618?w=800&h=600&fit=crop";

  return (
    <Layout>
      <div className="space-y-6" data-testid="scooter-detail">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/stock")}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-sm text-primary">{scooter.scooter_id}</span>
                <StatusBadge status={scooter.status} />
              </div>
              <h1 className="font-heading font-black text-2xl md:text-3xl">
                {scooter.brand} {scooter.model}
              </h1>
              <p className="text-muted-foreground font-mono text-sm mt-1">
                {scooter.year} • {scooter.engine_cc}cc • {scooter.color}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <PrintScooterSheet scooter={scooter} />
            <Link to={`/stock/${scooterId}/edit`}>
              <Button variant="outline" data-testid="btn-edit">
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </Link>
            {/* Delete button - Admin only */}
            {isAdmin() && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="text-destructive hover:text-destructive" data-testid="btn-delete">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer ce scooter ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Cette action supprimera définitivement {scooter.brand} {scooter.model} ({scooter.scooter_id}).
                      Cette action est irréversible.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                      Supprimer
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Gallery & QR */}
          <div className="lg:col-span-2 space-y-6">
            {/* Photo Gallery */}
            <Card className="border-border">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Camera className="w-5 h-5 text-primary" />
                    Photos / Fotografii
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? `${uploadProgress.current}/${uploadProgress.total}...` : "Upload Photos"}
                  </Button>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {uploading && (
                  <div className="mb-4 p-4 bg-primary/10 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-primary">
                        📤 Compression & Upload...
                      </p>
                      <p className="text-sm font-mono text-primary">
                        {uploadProgress.current}/{uploadProgress.total}
                      </p>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      Les photos sont compressées automatiquement pour un upload plus rapide
                    </p>
                  </div>
                )}
                {scooter.photos?.length > 0 ? (
                  <SortablePhotoGallery
                    photos={scooter.photos}
                    onDelete={handleDeletePhoto}
                    onReorder={async (newOrder) => {
                      try {
                        await scooterApi.reorderPhotos(scooterId, newOrder);
                        setScooter(prev => ({ ...prev, photos: newOrder }));
                        toast.success("Ordre des photos enregistré !");
                      } catch (error) {
                        toast.error("Erreur lors de la sauvegarde de l'ordre");
                        throw error;
                      }
                    }}
                    scooterId={scooterId}
                  />
                ) : (
                  <div className="aspect-video rounded-lg overflow-hidden bg-secondary relative">
                    <img
                      src={defaultImage}
                      alt="Default"
                      className="w-full h-full object-cover opacity-50"
                    />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <p className="text-muted-foreground text-sm">No photos uploaded</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Documents */}
            <Card className="border-border">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileText className="w-5 h-5 text-primary" />
                    Documents / Documente
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => docInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </Button>
                  <input
                    ref={docInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleDocUpload}
                    className="hidden"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {scooter.documents?.length > 0 ? (
                  <div className="space-y-2">
                    {scooter.documents.map((doc, idx) => {
                      const filename = doc.split("/").pop();
                      return (
                        <div key={idx} className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-muted-foreground" />
                            <span className="font-mono text-sm truncate max-w-[200px]">{filename}</span>
                          </div>
                          <div className="flex gap-2">
                            <a
                              href={getFileUrl(doc)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button variant="ghost" size="sm">
                                <Download className="w-4 h-4" />
                              </Button>
                            </a>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteDoc(doc)}
                              className="text-destructive hover:text-destructive"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-8">
                    No documents uploaded
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Details */}
          <div className="space-y-6">
            {/* Price Card - Admin only */}
            {isAdmin() ? (
              <Card className="border-border bg-gradient-to-br from-card to-card/50">
                <CardContent className="p-6">
                  <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">
                    Prix de vente
                  </p>
                  <p className="font-mono font-bold text-3xl text-primary">
                    {formatPrice(scooter.selling_price)}
                  </p>
                  <Separator className="my-4" />
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Prix d'achat</span>
                    <span className="font-mono">{formatPrice(scooter.purchase_price)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-2">
                    <span className="text-muted-foreground">Frais</span>
                    <span className="font-mono">{formatPrice(scooter.costs)}</span>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-border bg-secondary/50">
                <CardContent className="p-6 text-center">
                  <Lock className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Accès aux prix réservé aux administrateurs
                  </p>
                </CardContent>
              </Card>
            )}

            {/* QR Code */}
            <Card className="border-border">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <QrCode className="w-5 h-5 text-primary" />
                  QR Code
                </CardTitle>
              </CardHeader>
              <CardContent className="flex justify-center">
                <QRCodeDisplay 
                  scooterId={scooter.scooter_id} 
                  scooterName={`${scooter.brand} ${scooter.model} ${scooter.year}`}
                  size={150} 
                />
              </CardContent>
            </Card>

            {/* Quick Info */}
            <Card className="border-border">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Gauge className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Mileage</span>
                  <span className="ml-auto font-mono">{scooter.mileage.toLocaleString()} km</span>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Year</span>
                  <span className="ml-auto font-mono">{scooter.year}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Fuel className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Engine</span>
                  <span className="ml-auto font-mono">{scooter.engine_cc}cc</span>
                </div>
                <div className="flex items-center gap-3">
                  <Palette className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Color</span>
                  <span className="ml-auto font-mono">{scooter.color}</span>
                </div>
                <Separator />
                <div className="flex items-center gap-3">
                  <Hash className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">VIN</span>
                  <span className="ml-auto font-mono text-xs">{scooter.vin}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Tabs for Technical, Mechanical, History, Service, Sales, Publishing */}
        <Tabs defaultValue={scooter.status === "reception" || scooter.status === "preparation" ? "preparation" : "technical"} className="w-full">
          <TabsList className="w-full justify-start flex-wrap">
            {(scooter.status === "reception" || scooter.status === "preparation") && (
              <TabsTrigger value="preparation" className="flex items-center gap-2" data-testid="tab-preparation">
                <PackageOpen className="w-4 h-4" />
                Preparation
              </TabsTrigger>
            )}
            <TabsTrigger value="technical" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Technique
            </TabsTrigger>
            <TabsTrigger value="mechanical" className="flex items-center gap-2">
              <Wrench className="w-4 h-4" />
              Mecanique
            </TabsTrigger>
            <TabsTrigger value="service" className="flex items-center gap-2" data-testid="tab-service">
              <Droplet className="w-4 h-4" />
              Entretien
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="w-4 h-4" />
              Historique
            </TabsTrigger>
            {canPublish() && (
              <TabsTrigger value="publish" className="flex items-center gap-2" data-testid="tab-publish">
                <Share2 className="w-4 h-4" />
                Publication
              </TabsTrigger>
            )}
            {isAdmin() && (
              <TabsTrigger value="sale" className="flex items-center gap-2" data-testid="tab-sale">
                <ShoppingCart className="w-4 h-4" />
                Vente
              </TabsTrigger>
            )}
          </TabsList>

          {/* Preparation Tab - for reception/preparation status */}
          <TabsContent value="preparation">
            <div className="space-y-6">
              {/* Reception Info Card */}
              <Card className="border-purple-500/30 bg-purple-500/5">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <PackageOpen className="w-5 h-5 text-purple-500" />
                    Informations de Reception
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Code Scooter</p>
                      <p className="font-mono font-bold text-lg">{scooter.reception_data?.scooter_code || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Date Reception</p>
                      <p className="font-mono">{scooter.reception_data?.reception_date || scooter.date_added || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Etat a la Reception</p>
                      <p className="font-mono">{"★".repeat(scooter.reception_data?.reception_condition || 3)}{"☆".repeat(5 - (scooter.reception_data?.reception_condition || 3))}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Priorite</p>
                      <p className={`font-bold ${
                        scooter.reception_data?.repair_priority === 1 ? "text-red-500" :
                        scooter.reception_data?.repair_priority === 2 ? "text-yellow-500" : "text-blue-500"
                      }`}>
                        {scooter.reception_data?.repair_priority === 1 ? "Urgent" :
                         scooter.reception_data?.repair_priority === 2 ? "Normal" : "Peut attendre"}
                      </p>
                    </div>
                  </div>
                  {scooter.reception_data?.reception_notes && (
                    <div className="mt-4 p-3 bg-secondary/50 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Remarques de reception</p>
                      <p className="text-sm">{scooter.reception_data.reception_notes}</p>
                    </div>
                  )}
                  {scooter.reception_data?.container_reference && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      Conteneur: {scooter.reception_data.container_reference}
                      {scooter.reception_data?.supplier && ` | Fournisseur: ${scooter.reception_data.supplier}`}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Repair Tasks Manager */}
              <RepairTasksManager
                scooterId={scooterId}
                receptionData={scooter.reception_data}
                onUpdate={fetchScooter}
              />

              {/* Mark Ready for Sale Button */}
              {isAdmin() && (
                <Card className="border-green-500/30 bg-green-500/5">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          Pret a la vente ?
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Marquez ce scooter comme pret lorsque toutes les reparations sont terminees
                        </p>
                      </div>
                      <Button
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={async () => {
                          try {
                            const response = await scooterApi.markReadyForSale(scooterId, scooter.selling_price);
                            if (response.success) {
                              toast.success("Scooter pret a la vente !");
                              fetchScooter();
                            } else {
                              toast.warning(response.message);
                            }
                          } catch (error) {
                            toast.error("Erreur lors de la mise a jour");
                          }
                        }}
                        data-testid="btn-mark-ready-sale"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Marquer Pret
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="technical">
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(scooter.technical_file || {}).map(([key, value]) => (
                    <div key={key}>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        {key.replace(/_/g, " ")}
                      </p>
                      <p className="font-mono mt-1">{value || "-"}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="mechanical">
            <Card className="border-border">
              <CardContent className="p-6">
                {/* Average Score */}
                {scooter.mechanical_check && (
                  <div className="mb-6 p-4 bg-secondary/50 rounded-lg flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        Score moyen / Average Score
                      </p>
                      <p className={`font-mono font-bold text-2xl ${getOverallCondition(calculateAverageScore(scooter.mechanical_check)).color}`}>
                        {calculateAverageScore(scooter.mechanical_check)} / 5
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        État général
                      </p>
                      <p className={`font-bold ${getOverallCondition(calculateAverageScore(scooter.mechanical_check)).color}`}>
                        {getOverallCondition(calculateAverageScore(scooter.mechanical_check)).label}
                      </p>
                    </div>
                  </div>
                )}
                <MechanicalChecklistDisplay values={scooter.mechanical_check} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="service" data-testid="service-tab-content">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <OilChangeHistory 
                scooter={scooter} 
                onUpdate={fetchScooter}
              />
              <ServiceAlerts 
                scooter={scooter} 
                onUpdate={fetchScooter}
              />
            </div>
          </TabsContent>

          <TabsContent value="history">
            <Card className="border-border">
              <CardContent className="p-6 space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">
                    Date Added / Data intrare stoc
                  </p>
                  <p className="font-mono mt-1">{scooter.date_added}</p>
                </div>
                {scooter.repairs_done && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">
                      Repairs Done / Reparații efectuate
                    </p>
                    <p className="mt-1">{scooter.repairs_done}</p>
                  </div>
                )}
                {scooter.warranty_info && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">
                      Warranty / Garanție
                    </p>
                    <p className="font-mono mt-1">{scooter.warranty_info}</p>
                  </div>
                )}
                {scooter.internal_notes && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">
                      Internal Notes / Note interne
                    </p>
                    <p className="mt-1">{scooter.internal_notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="publish" data-testid="publish-tab-content">
            <PublishingCenter 
              scooter={scooter} 
              onStatusUpdate={(newStatus) => {
                setScooter(prev => ({ ...prev, publish_status: newStatus }));
              }}
            />
          </TabsContent>

          <TabsContent value="sale" data-testid="sale-tab-content">
            <SalesCenter 
              scooter={scooter} 
              onSaleComplete={(saleData) => {
                setScooter(prev => ({ 
                  ...prev, 
                  status: 'sold',
                  sale_record: saleData 
                }));
              }}
            />
          </TabsContent>
        </Tabs>

        {/* AI Tools */}
        <Card className="border-border mt-6" data-testid="ai-tools-section">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="w-6 h-6 rounded bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center text-white text-xs font-bold">AI</span>
              Instrumente AI / GPT-5.6 Sol
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Text AI */}
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-bold">Texte AI</p>
            <AIDescriptionButton scooterId={scooter.scooter_id} />
            <AISocialPostButton scooterId={scooter.scooter_id} />
            <Separator />
            {/* Image AI */}
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-bold">Imagini AI</p>
            <AIBackgroundButton scooterId={scooter.scooter_id} />
            <AISocialBannerButton scooterId={scooter.scooter_id} />
            <AIPromoVisualButton scooterId={scooter.scooter_id} />
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
